/**
 * Safe reads of a Monaco text model's full content.
 *
 * Monaco marks a model above 256M characters as "too large for heap
 * operation" and throws `Operation would exceed heap memory limits` from
 * `getValue()` / `getLinesContent()` for the rest of its life. The input
 * gates (`editorTextLimits.ts`) keep such a model from ever being created via
 * file open or paste; this helper is the defence for every remaining path
 * (programmatic `setValue`, a future gate gap) so a read degrades to "no
 * text" instead of an uncaught error in an event handler.
 */

type ModelLike = {
  isTooLargeForHeapOperation?: () => boolean;
  getValueLength?: () => number;
  getValue: () => string;
  isDisposed?: () => boolean;
};

/** Monaco's `LARGE_FILE_HEAP_OPERATION_THRESHOLD` (0.44): 256M characters. */
const MONACO_HEAP_OPERATION_LIMIT_CHARS = 256 * 1024 * 1024;

/** True when Monaco would throw from `getValue()` on this model. */
export function isModelTooLargeToRead(model: ModelLike | null | undefined): boolean {
  if (!model) return false;
  if (typeof model.isTooLargeForHeapOperation === 'function') {
    return model.isTooLargeForHeapOperation();
  }
  if (typeof model.getValueLength === 'function') {
    return model.getValueLength() > MONACO_HEAP_OPERATION_LIMIT_CHARS;
  }
  return false;
}

/**
 * The model's text, or `null` when it is disposed, missing, or too large for
 * Monaco to hand out. Callers treat `null` as "nothing to read".
 */
export function readModelTextSafe(model: ModelLike | null | undefined): string | null {
  if (!model) return null;
  if (typeof model.isDisposed === 'function' && model.isDisposed()) return null;
  if (isModelTooLargeToRead(model)) return null;
  try {
    return model.getValue();
  } catch {
    return null;
  }
}
